/**
 * Copyright (c) 2025-2026 Four Hour Window LLC
 * All rights reserved.
 * 
 * This source code is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import React, { useState } from 'react';
import { Product } from '../types/Products';

interface EnhancedProductCardProps {
  product: Product;
  firebaseToken?: string;
  isAdmin?: boolean;
  collectionHandle?: string;
}

const EnhancedProductCard: React.FC<EnhancedProductCardProps> = ({
  product,
  firebaseToken,
  isAdmin,
  collectionHandle
}) => {
  const [selectedVariantIndex, setSelectedVariantIndex] = useState(0);
  const [showPriceBreakdown, setShowPriceBreakdown] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);

  // Debug: Log all variant data on mount
  React.useEffect(() => {
    console.log('=== Product Variants ===');
    product.variants?.forEach((variant, index) => {
      console.log(`Variant ${index}:`, {
        id: variant.id,
        title: variant.title,
        option1: variant.option1,
        option2: variant.option2,
        option3: variant.option3,
        imageId: variant.imageId,
        cost: variant.cost,
        shopifyInventoryCost: variant.shopifyInventoryCost,
        printfulRetailPrice: variant.printfulRetailPrice,
        printfulMatchedBy: variant.printfulMatchedBy,
        allKeys: Object.keys(variant)
      });
    });
  }, [product.variants]);

  // Get the selected variant
  const selectedVariant = product.variants?.[selectedVariantIndex] || product.variants?.[0];
  
  // Parse pricing data
  const retailPrice = selectedVariant ? parseFloat(selectedVariant.price) : 0;

  // Use nullish coalescing so a cost of "0" is handled correctly
  const hasCostData = selectedVariant?.cost != null && selectedVariant.cost !== '';
  const baseCost = hasCostData ? parseFloat(selectedVariant.cost) : 0;
  
  // Calculate gross profit before platform fee
  const grossProfit = retailPrice - baseCost;
  
  // Calculate Merch Sherpa Platform fee (20% of gross profit)
  const merchSherpaFee = grossProfit * 0.20;
  
  // Calculate net profit after platform fee
  const netProfit = grossProfit - merchSherpaFee;
  
  // Calculate profit margin based on net profit
  const profitMargin = retailPrice > 0 ? (netProfit / retailPrice) * 100 : 0;
  
  // Check for embroidery on product
  const hasEmbroidery = (): boolean => {
    if (product.tags && product.tags.length > 0) {
      const hasEmbroideryTag = product.tags.some(tag => 
        tag.toLowerCase().includes('embroidery') || 
        tag.toLowerCase().includes('embroidered')
      );
      if (hasEmbroideryTag) return true;
    }
    
    if (product.bodyHtml) {
      const descriptionLower = product.bodyHtml.toLowerCase();
      if (descriptionLower.includes('embroidery') || 
          descriptionLower.includes('embroidered')) {
        return true;
      }
    }
    
    if (product.title.toLowerCase().includes('embroidery') || 
        product.title.toLowerCase().includes('embroidered')) {
      return true;
    }
    
    return false;
  };

  // Helper function to normalize color names for comparison
  const normalizeColorName = (colorName: string): string => {
    return colorName.toLowerCase().trim().replace(/[\s-_]/g, '');
  };

  // Helper function to find variant option1 for a specific image
  const findVariantOption1ForImage = (imageIndex: number, visitedIndices = new Set<number>()): string => {
    if (!product.images || !product.variants || imageIndex < 0 || imageIndex >= product.images.length) {
      return '';
    }
    
    if (visitedIndices.has(imageIndex)) {
      return '';
    }
    visitedIndices.add(imageIndex);
    
    const image = product.images[imageIndex];
    
    // Strategy 1: Try direct imageId match
    for (const variant of product.variants) {
      if (variant.imageId && String(variant.imageId) === String(image.id)) {
        return variant.option1 || '';
      }
    }
    
    // Strategy 2: Try matching by normalized color names in filename/alt
    const imageAlt = (image.alt || image.altText || '').toLowerCase();
    const imageSrc = (image.src || '').toLowerCase();
    
    for (const variant of product.variants) {
      if (variant.option1) {
        const normalizedOption1 = normalizeColorName(variant.option1);
        const normalizedAlt = normalizeColorName(imageAlt);
        const normalizedSrc = normalizeColorName(imageSrc);
        
        if (normalizedAlt.includes(normalizedOption1) || normalizedSrc.includes(normalizedOption1)) {
          return variant.option1;
        }
      }
    }
    
    // Strategy 3: Recursively check adjacent images
    if (imageIndex === 0 && product.images.length > 1) {
      const nextOption1 = findVariantOption1ForImage(1, visitedIndices);
      if (nextOption1) return nextOption1;
    } else {
      if (imageIndex > 0) {
        const prevOption1 = findVariantOption1ForImage(imageIndex - 1, visitedIndices);
        if (prevOption1) return prevOption1;
      }
      if (imageIndex < product.images.length - 1) {
        const nextOption1 = findVariantOption1ForImage(imageIndex + 1, visitedIndices);
        if (nextOption1) return nextOption1;
      }
    }
    
    return '';
  };

  // Get image index from variant's color (option1) or imageId
  const getImageIndexForVariant = (variant: any): number => {
    if (!variant || !product.images || product.images.length === 0) {
      return 0;
    }
    
    // Strategy 1: Try imageId (if available)
    if (variant.imageId) {
      const imageIndex = product.images.findIndex(img => {
        return String(img.id) === String(variant.imageId);
      });
      
      if (imageIndex !== -1) {
        return imageIndex;
      }
    }
    
    // Strategy 2: Match by option1 (usually the color) in image alt text
    if (variant.option1 && product.images.length > 1) {
      const option1Lower = variant.option1.toLowerCase().trim();
      
      const imageIndex = product.images.findIndex(img => {
        const alt = (img.alt || img.altText || '').toLowerCase();
        const src = (img.src || '').toLowerCase();
        
        return alt.includes(option1Lower) || src.includes(option1Lower);
      });
      
      if (imageIndex !== -1) {
        return imageIndex;
      }
    }
    
    // Strategy 3: Create a mapping based on unique option1 values
    if (variant.option1 && product.variants) {
      const uniqueColors = [...new Set(
        product.variants.map(v => v.option1).filter(Boolean)
      )];
      
      const colorIndex = uniqueColors.indexOf(variant.option1);
      
      if (colorIndex !== -1 && colorIndex < product.images.length) {
        return colorIndex;
      }
    }
    
    return 0;
  };

  const currentImageIndex = getImageIndexForVariant(selectedVariant);

  const getCurrentImage = () => {
    if (!product.images || product.images.length === 0) {
      return product.image;
    }
    
    const imageIndex = selectedImageIndex !== null ? selectedImageIndex : currentImageIndex;
    const imageToShow = product.images[imageIndex];
    return imageToShow?.resized || imageToShow?.src || product.image;
  };

  const currentImage = getCurrentImage();
  
  const displayImageIndex = selectedImageIndex !== null ? selectedImageIndex : currentImageIndex;

  const handleVariantChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newIndex = parseInt(e.target.value);
    const newVariant = product.variants?.[newIndex];
    console.log(`📝 Variant changed to index ${newIndex}: ${newVariant?.title}`);
    setSelectedVariantIndex(newIndex);
    
    if (newVariant?.option1 && product.images && product.images.length > 1) {
      const matchingImageIndex = product.images.findIndex(image => {
        if (newVariant.imageId && String(image.id) === String(newVariant.imageId)) {
          return true;
        }
        
        const option1Lower = newVariant.option1.toLowerCase().trim();
        const imageAlt = (image.alt || image.altText || '').toLowerCase();
        const imageSrc = (image.src || '').toLowerCase();
        
        return imageAlt.includes(option1Lower) || imageSrc.includes(option1Lower);
      });
      
      if (matchingImageIndex !== -1) {
        setSelectedImageIndex(matchingImageIndex);
        console.log(`🖼️ Updated image to index ${matchingImageIndex} for variant option1: ${newVariant.option1}`);
        return;
      }
    }
    
    setSelectedImageIndex(null);
  };

  const handleThumbnailClick = (imageIndex: number, event?: React.MouseEvent<HTMLDivElement>) => {
    console.log(`🖼️ Thumbnail ${imageIndex} clicked`);
    
    setSelectedImageIndex(imageIndex);
    
    if (!product.variants) return;
    
    const thumbnailOption1 = event?.currentTarget.getAttribute('data-variant-option1');
    console.log(`📌 Thumbnail data-variant-option1: "${thumbnailOption1}"`);
    
    if (thumbnailOption1) {
      console.log('Available variants:', product.variants.map((v, i) => `${i}: "${v.option1}"`));
      
      const matchingVariantIndex = product.variants.findIndex(
        variant => variant.option1 === thumbnailOption1
      );
      
      console.log(`🔍 Matching variant index: ${matchingVariantIndex}`);
      
      if (matchingVariantIndex !== -1) {
        console.log(`✅ Selecting variant ${matchingVariantIndex}: ${product.variants[matchingVariantIndex].title}`);
        setSelectedVariantIndex(matchingVariantIndex);
        return;
      } else {
        console.warn(`❌ No exact match found for option1: "${thumbnailOption1}"`);
      }
    } else {
      console.warn(`❌ No data-variant-option1 attribute found on thumbnail`);
    }
    
    console.log(`⚠️ No variant match for image ${imageIndex}, keeping current variant`);
  };

  const getVariantDisplayName = (variant: any) => {
    if (variant.title === 'Default Title') {
      return 'Standard';
    }
    return variant.title;
  };

  return (
    <div className="product-card" style={{
      border: '1px solid #e0e0e0',
      borderRadius: '8px',
      overflow: 'hidden',
      backgroundColor: 'white',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      transition: 'box-shadow 0.2s',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Product Image */}
      <div style={{ 
        position: 'relative',
        paddingBottom: '100%',
      }}>
        {/* LIVE STATUS BADGE - Upper Left */}
        <div style={{
          position: 'absolute',
          top: '10px',
          left: '10px',
          backgroundColor: product.status === 'active' ? 'rgb(78,137,88)' : '#6c757d',
          color: 'white',
          padding: '4px 8px',
          borderRadius: '4px',
          fontSize: '11px',
          fontWeight: 'bold',
          textTransform: 'uppercase',
          zIndex: 1
        }}>
          {product.status === 'active' ? '● LIVE' : '○ ' + (product.status || 'DRAFT')}
        </div>

        {currentImage && (
          <img
            src={currentImage}
            alt={product.title}
            style={{
              position: 'absolute',
              top: '15px',
              left: 0,
              width: '100%',
              height: 'calc(100% - 100px)',
              objectFit: 'contain',
              transition: 'opacity 0.3s ease-in-out'
            }}
            key={displayImageIndex}
          />
        )}
        
        {/* Sale Badge */}
        {product.onSale && (
          <div style={{
            position: 'absolute',
            top: '10px',
            right: '10px',
            backgroundColor: '#dc3545',
            color: 'white',
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: 'bold'
          }}>
            SALE
          </div>
        )}

        {/* Image Thumbnails */}
        {product.images && product.images.length > 1 && (
          <div style={{
            position: 'absolute',
            bottom: '8px',
            left: '8px',
            right: '8px',
            display: 'flex',
            gap: '6px',
            overflowX: 'auto',
            padding: '4px',
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            borderRadius: '4px',
            scrollbarWidth: 'thin'
          }}>
            {product.images.map((image, index) => {
              console.log(`=== Image ${index} Full Data ===`, {
                id: image.id,
                imageId: image.imageId,
                src: image.src,
                alt: image.alt,
                altText: image.altText,
                position: (image as any).position,
                width: (image as any).width,
                height: (image as any).height,
                allKeys: Object.keys(image)
              });
              
              const imageOption1 = findVariantOption1ForImage(index);
              
              if (imageOption1) {
                console.log(`Image ${index} has variant option1: ${imageOption1}`);
              } else {
                console.warn(`⚠️ Image ${index} has no matching variant option1`);
              }
              
              return (
                <div
                  key={image.id}
                  onClick={(e) => handleThumbnailClick(index, e)}
                  data-variant-option1={imageOption1}
                  style={{
                    minWidth: '50px',
                    width: '50px',
                    height: '50px',
                    borderRadius: '4px',
                    overflow: 'hidden',
                    cursor: 'pointer',
                    border: displayImageIndex === index ? '2px solid #007bff' : '2px solid transparent',
                    transition: 'border 0.2s',
                    flexShrink: 0
                  }}
                >
                  <img
                    src={image.resized || image.src}
                    alt={`${product.title} - view ${index + 1}`}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Product Details */}
      <div style={{ padding: '16px', flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Title */}
        <h3 style={{ 
          margin: '0 0 8px 0', 
          fontSize: '16px',
          fontWeight: '600',
          lineHeight: '1.4'
        }}>
          {product.title}
        </h3>

        {/* Vendor & Type */}
        {(product.vendor || product.productType) && (
          <div style={{ 
            fontSize: '13px', 
            color: '#666',
            marginBottom: '12px'
          }}>
            {product.vendor && <span>{product.vendor}</span>}
            {product.vendor && product.productType && <span> • </span>}
            {product.productType && <span>{product.productType}</span>}
          </div>
        )}

        {/* Variant Selector */}
        {product.variants && product.variants.length > 1 && (
          <div style={{ marginBottom: '12px' }}>
            <label style={{ 
              fontSize: '12px', 
              color: '#666',
              display: 'block',
              marginBottom: '4px'
            }}>
              Select Option:
            </label>
            <select
              value={selectedVariantIndex}
              onChange={handleVariantChange}
              style={{
                width: '100%',
                padding: '6px 8px',
                fontSize: '14px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                backgroundColor: 'white',
                cursor: 'pointer'
              }}
            >
              {product.variants.map((variant, index) => (
                <option 
                  key={variant.id} 
                  value={index}
                  data-image-id={variant.imageId || ''}
                >
                  {getVariantDisplayName(variant)} - ${parseFloat(variant.price).toFixed(2)}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Pricing Section */}
        <div>
          {/* Retail Price */}
          <div style={{ 
            fontSize: '24px', 
            fontWeight: 'bold',
            color: '#333',
            marginBottom: hasCostData ? '8px' : '0'
          }}>
            ${retailPrice.toFixed(2)}
            {selectedVariant?.compareAtPrice && parseFloat(selectedVariant.compareAtPrice) > retailPrice && (
              <span style={{
                fontSize: '16px',
                color: '#999',
                textDecoration: 'line-through',
                marginLeft: '8px',
                fontWeight: 'normal'
              }}>
                ${parseFloat(selectedVariant.compareAtPrice).toFixed(2)}
              </span>
            )}
          </div>

          {/* Cost & Profit Info */}
          {hasCostData && (
            <div>
              {/* Quick Profit View */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '8px',
                backgroundColor: '#f8f9fa',
                borderRadius: '4px',
                marginBottom: '8px'
              }}>
                <div style={{ fontSize: '13px', color: '#666' }}>
                  <strong>Your Estimated Profit:</strong>
                </div>
                <div style={{
                  fontSize: '16px',
                  fontWeight: 'bold',
                  color: netProfit > 0 ? '#4e8958' : '#dc3545'
                }}>
                  ${netProfit.toFixed(2)}
                  <span style={{ 
                    fontSize: '12px', 
                    marginLeft: '4px',
                    fontWeight: 'normal'
                  }}>
                    ({profitMargin.toFixed(1)}%)
                  </span>
                </div>
              </div>

              {/* Toggle for detailed breakdown */}
              <button className="price-breakdown"
                onClick={() => setShowPriceBreakdown(!showPriceBreakdown)}
              >
                {showPriceBreakdown ? '− Hide' : '+ Show'} Price Breakdown
              </button>

              {/* Detailed Price Breakdown */}
              {showPriceBreakdown && (
                <div style={{
                  marginTop: '8px',
                  padding: '12px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '6px',
                  fontSize: '13px',
                  border: '1px solid #e9ecef'
                }}>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    marginBottom: '8px',
                    paddingBottom: '8px',
                    borderBottom: '1px solid #dee2e6'
                  }}>
                    <span style={{ fontWeight: '600' }}>Price Breakdown</span>
                  </div>
                  
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    marginBottom: '6px'
                  }}>
                    <span>Retail Price (Shopify):</span>
                    <span style={{ fontWeight: 'bold' }}>
                      ${retailPrice.toFixed(2)}
                    </span>
                  </div>

                  {/* Show Printful retail price if available and different from Shopify */}
                  {selectedVariant?.printfulRetailPrice && (
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      marginBottom: '6px',
                      color: '#666',
                      fontSize: '12px'
                    }}>
                      <span>
                        Printful Retail Price
                        {selectedVariant.printfulMatchedBy && (
                          <span style={{ color: '#aaa', marginLeft: '4px' }}>
                            (matched by {selectedVariant.printfulMatchedBy})
                          </span>
                        )}
                        :
                      </span>
                      <span>${parseFloat(selectedVariant.printfulRetailPrice).toFixed(2)}</span>
                    </div>
                  )}
                  
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    marginBottom: '6px',
                    color: '#dc3545'
                  }}>
                    <span>Base Cost:</span>
                    <span>-${baseCost.toFixed(2)}</span>
                  </div>

                  {/* Show Shopify inventory cost if available alongside Printful */}
                  {selectedVariant?.shopifyInventoryCost != null && selectedVariant?.printfulRetailPrice != null && (
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      marginBottom: '6px',
                      color: '#666',
                      fontSize: '12px'
                    }}>
                      <span>Shopify Inventory Cost:</span>
                      <span>${parseFloat(selectedVariant.shopifyInventoryCost).toFixed(2)}</span>
                    </div>
                  )}
                  
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    marginBottom: '8px',
                    paddingBottom: '8px',
                    borderBottom: '1px dashed #dee2e6',
                    color: '#666',
                    fontSize: '12px'
                  }}>
                    <span>Gross Profit:</span>
                    <span>${grossProfit.toFixed(2)}</span>
                  </div>
                  
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    marginBottom: '6px',
                    color: '#dc3545'
                  }}>
                    <span>Merch Sherpa Platform Fee (20%):</span>
                    <span>-${merchSherpaFee.toFixed(2)}</span>
                  </div>
                  
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    paddingTop: '8px',
                    borderTop: '1px solid #dee2e6',
                    color: netProfit > 0 ? '#4e8958' : '#dc3545',
                    fontWeight: 'bold',
                    fontSize: '14px'
                  }}>
                    <span>Your Net Profit:</span>
                    <span>${netProfit.toFixed(2)}</span>
                  </div>

                  <div style={{
                    marginTop: '8px',
                    padding: '8px',
                    backgroundColor: netProfit > 0 ? '#d4edda' : '#f8d7da',
                    borderRadius: '4px',
                    fontSize: '12px',
                    color: netProfit > 0 ? '#155724' : '#721c24'
                  }}>
                    Estimated Profit Margin: <strong>{profitMargin.toFixed(1)}%</strong>
                    {netProfit <= 0 && (
                      <div style={{ marginTop: '4px', fontSize: '11px' }}>
                        ⚠️ Consider adjusting the retail price
                      </div>
                    )}
                  </div>

                  {/* Additional variant info */}
                  {selectedVariant?.sku && (
                    <div style={{ 
                      marginTop: '8px',
                      paddingTop: '8px',
                      borderTop: '1px solid #dee2e6',
                      fontSize: '12px',
                      color: '#666'
                    }}>
                      <div><strong>SKU:</strong> {selectedVariant.sku}</div>
                      {selectedVariant.inventoryQuantity !== undefined && (
                        <div style={{ marginTop: '4px' }}>
                          <strong>Stock:</strong> {selectedVariant.inventoryQuantity} units
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* No cost data message */}
          {!hasCostData && (
            <div style={{
              fontSize: '12px',
              color: '#856404',
              backgroundColor: '#fff3cd',
              padding: '8px',
              borderRadius: '4px',
              marginTop: '8px'
            }}>
              Cost data not available for this product
            </div>
          )}

          {hasEmbroidery() && (
            <div className="embroidery-warning">
              <strong>⚠️ Note:</strong> First time embroidery charge is not reflected
            </div>
          )}

          {/* Inventory Status */}
          {selectedVariant?.inventoryQuantity !== undefined && (
            <div style={{ 
              marginTop: '12px',
              fontSize: '13px',
              color: selectedVariant.inventoryQuantity > 0 ? '#4e8958' : '#dc3545'
            }}>
              {selectedVariant.inventoryQuantity > 0 
                ? `✓ In Stock (${selectedVariant.inventoryQuantity})` 
                : '✗ Out of Stock'}
            </div>
          )}
        </div>
      </div>

      {/* Footer Actions */}
      <div style={{
        padding: '12px 16px',
        borderTop: '1px solid #e0e0e0',
        backgroundColor: '#fafafa'
      }}>
        {isAdmin && 
          <a className="btn-product-link admin" href={`https://admin.shopify.com/store/merch-sherpa/products/${product.id}`} target="_blank" rel="noopener noreferrer">
            Edit on Shopify
          </a>
        }
        <a className="btn-product-link"
          href={`https://merch-sherpa.com/products/${product.handle}${selectedVariant ? `?variant=${selectedVariant.id}` : ''}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          View in Store
        </a>
      </div>
    </div>
  );
};

export default EnhancedProductCard;